<template>
    <div class="notice-toast" :class="variantClass" role="status" aria-live="polite">
        <button type="button" class="notice-toast__close" :aria-label="closeLabel" @click="requestClose">
            <i class="i-andy-close i-andy-s" aria-hidden="true"></i>
        </button>
        <p v-if="title" class="notice-toast__title">{{ title }}</p>
        <p v-if="message" class="notice-toast__message">{{ message }}</p>
        <div v-if="hasDuration" class="notice-toast__progress" aria-hidden="true">
            <div ref="progressBar" class="notice-toast__progress-bar"></div>
        </div>
    </div>
</template>

<script>
import { soundService } from '@/services/soundService'

let noticeSoundSequence = 0

export default {
    name: 'NoticeToast',
    props: {
        title: {
            type: String,
            default: ''
        },
        message: {
            type: String,
            default: ''
        },
        duration: {
            type: Number,
            default: 0
        },
        sound: {
            type: String,
            default: ''
        },
        variant: {
            type: String,
            default: ''
        },
        closeLabel: {
            type: String,
            default: 'Cerrar'
        }
    },
    data() {
        return {
            closeTimer: null,
            closed: false
        }
    },
    computed: {
        variantClass() {
            if (!this.variant) {
                return null
            }

            return `notice-toast--${this.variant}`
        },
        hasDuration() {
            return Number.isFinite(this.duration) && this.duration > 0
        }
    },
    mounted() {
        this.playSound()
        this.scheduleClose()
        this.startProgress()
    },
    beforeDestroy() {
        this.clearCloseTimer()
        this.clearProgressAnimation()
    },
    methods: {
        playSound() {
            if (!this.sound) {
                return
            }

            noticeSoundSequence += 1
            soundService.playNotificationSound(this.sound, 1, `notice-toast-${noticeSoundSequence}`)
        },
        scheduleClose() {
            if (!this.hasDuration) {
                return
            }

            this.closeTimer = setTimeout(() => {
                this.closeTimer = null
                this.requestClose()
            }, this.duration)
        },
        requestClose() {
            if (this.closed) {
                return
            }

            this.closed = true
            this.clearCloseTimer()
            this.clearProgressAnimation()
            this.$emit('close')
        },
        startProgress() {
            const progressBar = this.$refs.progressBar

            if (!this.hasDuration || !progressBar || typeof progressBar.animate !== 'function') {
                return
            }

            this.progressAnimation = progressBar.animate(
                [{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }],
                {
                    duration: this.duration,
                    easing: 'linear',
                    fill: 'forwards'
                }
            )
        },
        clearCloseTimer() {
            if (!this.closeTimer) {
                return
            }

            clearTimeout(this.closeTimer)
            this.closeTimer = null
        },
        clearProgressAnimation() {
            if (!this.progressAnimation) {
                return
            }

            this.progressAnimation.cancel()
            this.progressAnimation = null
        }
    }
}
</script>

<style lang="scss" scoped>
$notice-toast-padding-x: 20px;

.notice-toast {
    position: absolute;
    z-index: 3;
    left: 16px;
    right: 16px;
    bottom: 16px;
    box-sizing: border-box;
    padding: 16px $notice-toast-padding-x;
    border-radius: 4px;
    background-color: $error-light;
    color: $neutro-s80;
    pointer-events: none;

    &__close {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: 0;
        border-radius: 4px;
        background-color: transparent;
        color: $neutro-s80;
        pointer-events: auto;
        cursor: pointer;

        &:hover {
            background-color: transparent;
            color: $neutro-s80;
        }

        &:focus {
            outline: 2px solid $neutro-s80;
            outline-offset: 2px;
        }
    }

    &__title,
    &__message {
        margin: 0;
        padding-right: 28px;
    }

    &__title {
        font-family: $text-bold;
        @include font-size(l);
        line-height: 1.3;
    }

    &__message {
        margin-top: 4px;
        font-family: $text;
        @include font-size(m);
        line-height: 1.4;
    }

    &__progress {
        position: absolute;
        right: $notice-toast-padding-x;
        bottom: 8px;
        left: $notice-toast-padding-x;
        height: 4px;
        background-color: $error-dark;
    }

    &__progress-bar {
        width: 100%;
        height: 100%;
        background-color: $neutro-t30;
        transform-origin: right center;
    }
}
</style>
